import React, { useCallback, useEffect, useState } from 'react';

import { useIntl } from 'react-intl';

import ActionButton, { ActionButtonVariants } from 'components/action-button';
import { IDialogProps } from 'components/dialog';
import SanityBlockRenderer, { IBlockContentOverrides } from 'components/sanity-block-renderer';
import { useCreateLoyaltyUserMutation } from 'generated/rbi-graphql';
import useEffectOnUpdates from 'hooks/use-effect-on-updates';
import { useAuthContext } from 'state/auth';
import { useLocale } from 'state/intl';
import { useLocationContext } from 'state/location';
import { useIsLoyaltyEnabled } from 'state/loyalty/hooks/use-is-loyalty-enabled';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';
import { usePageManagerContext } from 'state/static-page-manager';

import { HAS_DEFAULT_IMAGE_ANIMATION } from './constants';
import { ActionWrapper, ImageContainer, ParagraphOptOut } from './loyalty-opt-in-modal.styled';
import OptInDefaultAnimation from './opt-in-default-animation';
import { useFeaturesLoyaltyOptInModal } from './use-features-loyalty-opt-in-modal';
import getOptInModalLoadingProps from './utils/getOptInModalLoadingProps';

const blockTextContentOverrides: IBlockContentOverrides = {
  normal: ParagraphOptOut,
};

// Maximum amount of times the user can retry account creation before a close
// button is shown.
const MAX_RETRY_TIMES = 3;

// Max timeout to wait for loyalty user data to be fetched before finishing
// up opt in flow.
const MAX_LOYALTY_USER_FETCH_TIMEOUT = 5000;

export const useLoyaltyOptIn = () => {
  const { data: modalData, loading: loadingData } = useFeaturesLoyaltyOptInModal();
  const { language } = useLocale();
  const { user, refreshCurrentUser } = useAuthContext();
  const loyaltyEnabled = useIsLoyaltyEnabled();
  const { loyaltyUser } = useLoyaltyUser();
  const { logEvent } = useMParticleContext();
  const [dialogProps, setDialogProps] = useState<Partial<IDialogProps> | undefined>();
  const [isCompleted, setIsCompleted] = useState(false);
  const [retryTimes, setRetryTimes] = useState(0);
  const [isDismissed, setIsDismissed] = useState(false);
  const { navigate } = useLocationContext();
  const { formatMessage } = useIntl();
  const [loyaltyUserCreated, setLoyaltyUserCreated] = useState(false);
  const [createLoyaltyUser] = useCreateLoyaltyUserMutation({
    onCompleted: ({ createLoyaltyUser: { loyaltyId } }) => {
      if (!loyaltyId) {
        return;
      }
      // Reload user data since loyalty id should now be updated
      refreshCurrentUser();

      setLoyaltyUserCreated(true);
      logEvent(CustomEventNames.LOYALTY_ACKNOWLEDGEMENT, EventTypes.Other, {
        'Is Successful': true,
      });
    },
    onError: () => {
      logEvent(CustomEventNames.LOYALTY_ACKNOWLEDGEMENT, EventTypes.Other, {
        'Is Successful': false,
      });
      setRetryTimes(val => val + 1);
    },
  });
  const cognitoId = user?.cognitoId;

  // Reset loyalty opt in state when user changes
  useEffectOnUpdates(() => {
    setIsCompleted(false);
    setLoyaltyUserCreated(false);
    setIsDismissed(false);
    setDialogProps(undefined);
    setRetryTimes(0);
  }, [cognitoId]);

  // Wait until we have loyalty user fetched before completing opt in flow
  useEffect(() => {
    if (!loyaltyUserCreated || isCompleted) {
      return;
    }
    const onCompleteOptIn = () => {
      setIsCompleted(true);
      navigate(formatMessage({ id: 'routes.loyaltyDashboard' }));
    };

    // Set timeout to wait for loyalty user to be fetched before
    // completing the opt-in flow. Without this, the user can get stuck
    // forever in a loading state if loyalty user fetch fails.
    // This can happen if the graphql gateway is down.
    let completeTimeout: NodeJS.Timeout | undefined;
    if (!loyaltyUser) {
      completeTimeout = global.setTimeout(() => {
        if (!isCompleted) {
          onCompleteOptIn();
        }
      }, MAX_LOYALTY_USER_FETCH_TIMEOUT);
    } else {
      onCompleteOptIn();
    }

    return () => {
      if (completeTimeout) {
        clearTimeout(completeTimeout);
      }
    };
  }, [formatMessage, isCompleted, loyaltyUser, loyaltyUserCreated, navigate]);

  const handleOptIn = useCallback(async () => {
    const optInModalLoadingData = modalData?.optInModalLoading;
    if (!optInModalLoadingData || !cognitoId) {
      return;
    }
    const optInModalLoadingDialogProps = getOptInModalLoadingProps({
      title: optInModalLoadingData.title?.locale!,
      description: optInModalLoadingData.description?.locale || undefined,
    });
    setDialogProps(optInModalLoadingDialogProps);
    createLoyaltyUser({ variables: { cognitoId } });
  }, [cognitoId, createLoyaltyUser, modalData]);

  // Handle errors
  useEffect(() => {
    const optInModalError = modalData?.optInModalError;
    if (!optInModalError || retryTimes <= 0) {
      return;
    }
    const errorImage = optInModalError.image?.locale?.app;
    const errorImageAlt = optInModalError.image?.locale?.imageDescription;
    const canDismiss = retryTimes > MAX_RETRY_TIMES;
    setDialogProps({
      showCloseButton: canDismiss,
      onDismiss: canDismiss ? () => setIsDismissed(true) : undefined,
      heading: optInModalError.title?.locale!,
      body: optInModalError.description?.locale || undefined,
      image: errorImage && (
        <ImageContainer image={errorImage} alt={errorImageAlt || 'Image'} objectFitContain />
      ),
      actions: (
        <ActionWrapper>
          <ActionButton fullWidth onClick={handleOptIn} variant={ActionButtonVariants.OUTLINE}>
            {optInModalError.buttonText?.locale}
          </ActionButton>{' '}
        </ActionWrapper>
      ),
    });
  }, [handleOptIn, modalData, retryTimes]);

  // Show opt in modal
  useEffect(() => {
    const optInModal = modalData?.optInModal;
    if (!optInModal || dialogProps || !cognitoId || !loyaltyEnabled) {
      return;
    }
    const modalImage = optInModal.image?.locale?.app;

    const dialogImg = HAS_DEFAULT_IMAGE_ANIMATION ? (
      <OptInDefaultAnimation />
    ) : (
      modalImage && (
        <ImageContainer
          image={modalImage}
          alt={optInModal.image?.locale?.imageDescription || 'Image'}
          objectFitContain
        />
      )
    );
    setDialogProps({
      heading: optInModal.title?.locale!,
      body: optInModal.description?.locale || undefined,
      disclaimer: (
        <SanityBlockRenderer
          content={optInModal.blockText?.localeRaw}
          blockContentOverrides={blockTextContentOverrides}
        />
      ),
      image: dialogImg,
      actions: (
        <ActionWrapper>
          <ActionButton
            data-testid="loyalty-opt-in-button"
            onlyIcon
            fullWidth
            onClick={handleOptIn}
          >
            {optInModal.buttonText?.locale}
          </ActionButton>
        </ActionWrapper>
      ),
    });
  }, [modalData, dialogProps, handleOptIn, cognitoId, loyaltyEnabled]);

  // Check if page is static to avoid showing loyalty opt in.
  const { routes } = usePageManagerContext();
  const {
    location: { pathname },
  } = useLocationContext();
  const isStaticPage = routes.some(
    route => `/${route.localePath?.[language]?.current}` === pathname
  );

  return {
    dialogProps,
    loadingData,
    isCompleted,
    hasData: !!modalData,
    isDismissed,
    loyaltyUserCreated,
    excludeForCurrentPath: isStaticPage,
  };
};
